import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';

const PublicNavigation = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = (dropdownName) => {
    if (dropdownTimeoutRef?.current) {
      clearTimeout(dropdownTimeoutRef?.current);
    }
    setOpenDropdown(dropdownName);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const navigationItems = [
    { label: 'Accueil', path: '/' },
    { 
      label: 'Nos Services', 
      dropdown: [
        { 
          label: 'Particuliers', 
          description: 'Envoi de barriques vers la Côte d\'Ivoire',
          path: '/particuliers',
          icon: 'Package'
        },
        { 
          label: 'Professionnels', 
          description: 'Envoi en volume, conteneur, EX1',
          path: '/professional-services-landing-page',
          badge: 'PRO',
          icon: 'Briefcase'
        }
      ]
    },
    { label: 'Tarifs', path: '/dedicated-pricing-page' },
    { label: 'Suivi', path: '/home-tracking' },
    { label: 'Blog', path: '/maritime-transport-blog-hub' },
    { label: 'À propos', path: '/shipping-information' }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <nav 
        className={`fixed top-0 md:top-10 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg' 
            : 'bg-white shadow-md'
        }`}
        style={{
          boxShadow: isScrolled ? '0 4px 30px rgba(0,0,0,0.08)' : '0 4px 30px rgba(0,0,0,0.05)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-24">
            {/* Logo - Mobile Optimized */}
            <Link to="/" className="flex items-center space-x-2 md:space-x-3 group">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg" style={{ boxShadow: '0 4px 15px rgba(22,163,74,0.25)' }}>
                <Icon name="Package" size={20} className="text-white md:hidden" />
                <Icon name="Package" size={24} className="text-white hidden md:block" />
              </div>
              <div className="flex items-baseline space-x-1.5">
                <span className="text-lg md:text-xl font-bold text-gray-900" style={{ fontWeight: 700, letterSpacing: '0.02em' }}>ADT</span>
                <span className="text-lg md:text-xl text-gray-900" style={{ fontWeight: 400, letterSpacing: '0.02em' }}>Express</span>
              </div>
            </Link>

            {/* Desktop Navigation Links - Center - INCREASED SPACING */}
            <div className="hidden lg:flex items-center space-x-2">
              {navigationItems?.map((item, index) => (
                <div 
                  key={index}
                  className="relative"
                  onMouseEnter={() => item?.dropdown && handleMouseEnter(item?.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item?.dropdown ? (
                    <>
                      <button
                        className="relative px-5 py-3 group flex items-center space-x-1.5 transition-all duration-300"
                      >
                        <span className="text-base font-semibold transition-colors duration-300 text-gray-700 group-hover:text-green-600">
                          {item?.label}
                        </span>
                        <Icon name="ChevronDown" size={16} className="text-gray-500 group-hover:text-green-600 transition-all duration-300" />
                      </button>
                      
                      {/* Dropdown Menu - ENHANCED DEPTH */}
                      <AnimatePresence>
                        {openDropdown === item?.label && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl border border-gray-100 overflow-hidden"
                            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}
                          >
                            {item?.dropdown?.map((dropdownItem, idx) => (
                              <Link
                                key={idx}
                                to={dropdownItem?.path}
                                className="flex items-start space-x-3 px-5 py-4 hover:bg-green-50 transition-all duration-300 group/item"
                              >
                                <div className="mt-1">
                                  <Icon name={dropdownItem?.icon} size={20} className="text-green-600" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-semibold text-gray-900 group-hover/item:text-green-600 transition-colors duration-300">
                                      {dropdownItem?.label}
                                    </span>
                                    {dropdownItem?.badge && (
                                      <span className="px-2 py-0.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full">
                                        {dropdownItem?.badge}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                                    {dropdownItem?.description}
                                  </p>
                                </div>
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={item?.path}
                      className="relative px-5 py-3 group"
                    >
                      <span className={`text-base font-semibold transition-colors duration-300 ${
                        isActivePath(item?.path)
                          ? 'text-green-600' : 'text-gray-700 group-hover:text-green-600'
                      }`}>
                        {item?.label}
                      </span>
                      {/* Animated underline - PREMIUM GREEN */}
                      <motion.div
                        className="absolute bottom-1 left-0 right-0 h-0.5 bg-green-600 rounded-full"
                        initial={false}
                        animate={{
                          scaleX: isActivePath(item?.path) ? 1 : 0,
                          opacity: isActivePath(item?.path) ? 1 : 0
                        }}
                        whileHover={{ scaleX: 1, opacity: 1 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      />
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Action Buttons - Right - CLEAR HIERARCHY */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Connexion Button - SECONDARY DISCREET */}
              <div 
                className="relative"
                onMouseEnter={() => handleMouseEnter('connexion')}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className="flex items-center space-x-2 px-5 py-2.5 rounded-lg bg-white border border-gray-200 text-gray-900 font-medium transition-all duration-300 hover:bg-gray-50 hover:border-gray-300 group"
                  style={{ fontWeight: 500 }}
                >
                  <Icon name="User" size={18} className="text-gray-600" />
                  <span>Connexion</span>
                  <Icon name="ChevronDown" size={16} className="text-gray-500" />
                </button>

                {/* Connexion Dropdown - ENHANCED */}
                <AnimatePresence>
                  {openDropdown === 'connexion' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl border border-gray-100 overflow-hidden"
                      style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}
                    >
                      <Link
                        to="/client-login"
                        className="flex items-start space-x-3 px-5 py-4 hover:bg-green-50 transition-all duration-300 group/item"
                      >
                        <div className="mt-1">
                          <Icon name="User" size={20} className="text-green-600" />
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-gray-900 group-hover/item:text-green-600 block transition-colors duration-300">
                            Espace Client
                          </span>
                          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                            Suivez vos envois et téléchargez vos factures
                          </p>
                        </div>
                      </Link>
                      <Link
                        to="/professional-client-login"
                        className="flex items-start space-x-3 px-5 py-4 hover:bg-green-50 transition-all duration-300 group/item"
                      >
                        <div className="mt-1">
                          <Icon name="Briefcase" size={20} className="text-green-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900 group-hover/item:text-green-600 transition-colors duration-300">
                              Espace Pro
                            </span>
                            <span className="px-2 py-0.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full">
                              PRO
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                            Accédez à votre tableau de bord professionnel
                          </p>
                        </div>
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Commander CTA Button - DOMINANT PREMIUM */}
              <Link
                to="/commander"
                className="flex items-center space-x-2 px-7 py-3 rounded-xl bg-green-600 text-white font-semibold transition-all duration-300 hover:bg-green-700 transform hover:scale-105 hover:-translate-y-0.5"
                style={{ 
                  fontWeight: 600,
                  boxShadow: '0 10px 25px rgba(22,163,74,0.3)'
                }}
              >
                <Icon name="ShoppingCart" size={18} />
                <span>Commander</span>
              </Link>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors touch-target"
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <motion.span
                  animate={{
                    rotate: isMobileMenuOpen ? 45 : 0,
                    y: isMobileMenuOpen ? 8 : 0
                  }}
                  className="w-full h-0.5 bg-gray-700 rounded-full origin-center"
                />
                <motion.span
                  animate={{
                    opacity: isMobileMenuOpen ? 0 : 1
                  }}
                  className="w-full h-0.5 bg-gray-700 rounded-full"
                />
                <motion.span
                  animate={{
                    rotate: isMobileMenuOpen ? -45 : 0,
                    y: isMobileMenuOpen ? -8 : 0
                  }}
                  className="w-full h-0.5 bg-gray-700 rounded-full origin-center"
                />
              </div>
            </button>
          </div>
        </div>
      </nav>
      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={toggleMobileMenu}
            />

            {/* Full Screen Menu */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed inset-y-0 right-0 w-full sm:w-80 bg-white z-50 lg:hidden overflow-y-auto shadow-2xl"
            >
              <div className="p-4 sm:p-6">
                {/* Close Button */}
                <div className="flex justify-between items-center mb-6">
                  <span className="text-lg md:text-xl font-bold text-gray-900">Menu</span>
                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors touch-target"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                {/* Mobile Navigation Links */}
                <div className="flex flex-col space-y-1 mb-6">
                  {navigationItems?.map((item, index) => (
                    <div key={index}>
                      {item?.dropdown ? (
                        <div className="space-y-1">
                          <div className="px-4 py-3 text-base font-bold text-gray-900">
                            {item?.label}
                          </div>
                          {item?.dropdown?.map((dropdownItem, idx) => (
                            <Link
                              key={idx}
                              to={dropdownItem?.path}
                              onClick={toggleMobileMenu}
                              className="flex items-center justify-between px-4 py-3 rounded-xl text-base font-semibold text-gray-700 hover:bg-green-50 hover:text-green-600 transition-all min-h-[48px]"
                            >
                              <div className="flex items-center space-x-2">
                                <Icon name={dropdownItem?.icon} size={18} />
                                <span>{dropdownItem?.label}</span>
                              </div>
                              {dropdownItem?.badge && (
                                <span className="px-2 py-0.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full">
                                  {dropdownItem?.badge}
                                </span>
                              )}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <Link
                          to={item?.path}
                          onClick={toggleMobileMenu}
                          className={`px-4 py-3 rounded-xl text-base font-semibold transition-all min-h-[48px] flex items-center ${
                            isActivePath(item?.path)
                              ? 'bg-green-50 text-green-600 border-l-4 border-green-600' : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {item?.label}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>

                {/* Mobile Action Buttons */}
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  {/* Commander - DOMINANT */}
                  <Link
                    to="/barrel-deposit-scheduling-system"
                    onClick={toggleMobileMenu}
                    className="flex items-center justify-center space-x-2 w-full px-6 py-4 rounded-xl bg-green-600 text-white font-semibold transition-all hover:bg-green-700 min-h-[48px]"
                    style={{ 
                      fontWeight: 600,
                      boxShadow: '0 8px 20px rgba(22,163,74,0.3)'
                    }}
                  >
                    <Icon name="ShoppingCart" size={20} />
                    <span>Commander</span>
                  </Link>

                  <div className="text-sm font-semibold text-gray-500 px-4 py-2">
                    Connexion
                  </div>

                  {/* Connexion options */}
                  <Link
                    to="/client-login"
                    onClick={toggleMobileMenu}
                    className="flex items-center justify-between px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 font-medium hover:bg-gray-50 transition-all min-h-[48px]"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="User" size={20} className="text-gray-600" />
                      <span>Espace Client</span>
                    </div>
                    <Icon name="ChevronRight" size={20} className="text-gray-400" />
                  </Link>

                  <Link
                    to="/professional-client-login"
                    onClick={toggleMobileMenu}
                    className="flex items-center justify-between px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 font-medium hover:bg-gray-50 transition-all min-h-[48px]"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="Briefcase" size={20} className="text-gray-600" />
                      <span>Espace Pro</span>
                      <span className="px-2 py-0.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full ml-2">
                        PRO
                      </span>
                    </div>
                    <Icon name="ChevronRight" size={20} className="text-gray-400" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default PublicNavigation;